import React from 'react';
import {
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  Wrap,
  WrapItem,
  useColorModeValue,
  Box,
  Icon,
} from '@chakra-ui/react';
import { FaBriefcase } from 'react-icons/fa';
import Select from 'react-select';

const DepartmentSelectionCard = ({
  addUser,
  roleConfig,
  loadingDepartments,
  mappedAvailableDepartments,
  departments,
  selectStyles,
  handleDepartmentChange,
  loginRole,
}) => {
  const modalTextColor = useColorModeValue('gray.800', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const cardBg = useColorModeValue('white', 'gray.800');

  if (!addUser.role) return null;

  const roleCfg = roleConfig[addUser.role];
  const isMultiSelect = roleCfg.departmentType === 'multiple';

  return (
    <Card
      mb={6}
      boxShadow="sm"
      border="1px solid"
      borderColor={borderColor}
      bg={cardBg}
    >
      <CardBody>
        <VStack align="start" spacing={4}>
          <HStack spacing={2}>
            <Icon as={FaBriefcase} color="teal.500" />
            <Text fontSize="lg" fontWeight="semibold" color={modalTextColor}>
              Step 2: Select Department{isMultiSelect ? 's' : ''}
            </Text>
          </HStack>

          <Text fontSize="sm" color={subtitleColor} mb={4}>
            {loginRole === 'schemeAdmin'
              ? 'Select from your assigned departments'
              : isMultiSelect
              ? 'Select one or more departments this user will manage'
              : 'Select the department this user will belong to'}
          </Text>

          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
              Department{isMultiSelect ? 's' : ''}
              {loadingDepartments && <Spinner size="sm" ml={2} />}
            </FormLabel>

            {loadingDepartments ? (
              <HStack spacing={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={subtitleColor}>
                  Loading departments...
                </Text>
              </HStack>
            ) : mappedAvailableDepartments.length === 0 ? (
              <Alert status="warning" size="sm">
                <AlertIcon />
                No departments available
              </Alert>
            ) : (
              <>
                <Select
                  options={mappedAvailableDepartments}
                  styles={selectStyles}
                  value={mappedAvailableDepartments.filter(
                    (dept) =>
                      addUser.department &&
                      addUser.department.includes(dept.value),
                  )}
                  onChange={handleDepartmentChange}
                  placeholder={
                    isMultiSelect
                      ? 'Select one or more departments'
                      : 'Select department'
                  }
                  isMulti={isMultiSelect}
                />
                {loginRole === 'schemeAdmin' && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    You can only assign departments that you have access to
                  </Text>
                )}
              </>
            )}
          </FormControl>

          {addUser.department.length > 0 && (
            <Box w="full">
              <Text fontSize="sm" fontWeight="medium" color={labelColor} mb={2}>
                Selected Department{addUser.department.length > 1 ? 's' : ''} (
                {addUser.department.length})
              </Text>
              <Wrap spacing={2}>
                {addUser.department.map((deptId) => {
                  const dept = departments.find((d) => d._id === deptId);
                  return (
                    <WrapItem key={deptId}>
                      <Badge
                        colorScheme="blue"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {dept?.name || 'Department'}
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default DepartmentSelectionCard;
